"use client";

// SFWL-005: dataSource is now an open string carrying the resolved provider
// id from the backend (e.g. "hyperliquid", "cqg_replay", future "cqg"),
// optionally with a "-ws" suffix when live ticks are streaming.
interface ConnectionStatusProps {
  isConnected: boolean;
  latency: number | null;
  dataSource: string | null;
  /**
   * FEAT-003 (docs/features/FEAT-003-trade-route-unmapped-market-crash.md):
   * false when the active market has no Hyperliquid coin mapping, so no
   * live candle/tick feed is possible for it regardless of `isConnected`
   * (backend-sourced data still works). Optional/defaults to `true` so
   * existing callers that haven't wired the hook's `hasLiveFeed` yet don't
   * show the indicator.
   */
  hasLiveFeed?: boolean;
}

function formatDataSource(source: string): { label: string; live: boolean } {
  const live = source.endsWith("-ws");
  const id = live ? source.slice(0, -3) : source;
  const friendly =
    id === "hyperliquid"
      ? "Hyperliquid"
      : id === "cqg"
        ? "CQG"
        : id === "cqg_replay"
          ? "CQG Replay"
          : id;
  return { label: `${friendly} ${live ? "Live" : "REST"}`, live };
}

export function ConnectionStatus({
  isConnected,
  latency,
  dataSource,
  hasLiveFeed = true,
}: ConnectionStatusProps) {
  const formatted = dataSource ? formatDataSource(dataSource) : null;

  return (
    <div className="flex items-center gap-3 text-xs">
      {/* Connection indicator */}
      <div className="flex items-center gap-1.5">
        <div
          className={`h-2 w-2 rounded-full ${
            isConnected ? "bg-green-500 animate-pulse" : "bg-red-500"
          }`}
        />
        <span className="text-slate-400">
          {isConnected ? (latency ? `${latency}ms` : "Live") : "Offline"}
        </span>
      </div>

      {/* Data source indicator */}
      {formatted && (
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">|</span>
          <span
            className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${
              formatted.live
                ? "bg-green-500/20 text-green-400"
                : "bg-blue-500/20 text-blue-400"
            }`}
          >
            {formatted.label}
          </span>
        </div>
      )}

      {/* FEAT-003: unmapped-market indicator — small and visually minor by
          design (OQ1: silent degradation was rejected, but this shouldn't
          compete with the primary connection/data-source indicators). */}
      {!hasLiveFeed && (
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">|</span>
          <div
            className="flex items-center gap-1"
            title="No Hyperliquid mapping for this market"
          >
            <div className="h-1.5 w-1.5 rounded-full bg-amber-500/70" />
            <span className="text-amber-400/80">Live data unavailable</span>
          </div>
        </div>
      )}
    </div>
  );
}
